// frontend/src/visio/bench/types.ts
// TypeScript types for the A/V Sync Bench feature.

/** One measurement cycle (one beep+flash within a burst sequence).
 *  2-point measurement: sentAt (1) -> local pipeline detection (2).
 *  Δ21 = avg(video) - avg(audio) — local pipeline desync. */
export interface BenchCycle {
  /** Cycle index (0-based, flat across all sequences in the pass). */
  index: number
  /** Burst offset within the sequence (ms). */
  burstOffsetMs: number
  /** Burst type: bip1 (50ms anchor) or bip2 (10ms short). */
  burstType: 'bip1' | 'bip2'
  /** (1) Timestamp when beep+flash was injected (performance.now). */
  sentAt: number
  /** (2a) Timestamp when beep was detected in the outgoing audio stream. */
  localAudioDetectedAt: number | null
  /** (2b) Timestamp when flash was detected in the outgoing video stream. */
  localVideoDetectedAt: number | null
  /** Local audio pipeline latency (ms): sentAt -> detection. */
  localAudioLatencyMs: number | null
  /** Local video pipeline latency (ms): sentAt -> detection. */
  localVideoLatencyMs: number | null
}

/** Denoise preset for bench pass. */
export type DenoisePreset = 'off' | 'sync' | 'async'

/** VBG mode for bench pass. */
export type VbgMode = 'none' | 'gpu' | 'cpu'

/** Configuration variant for one bench pass. */
export interface PassConfig {
  label: string
  denoise: DenoisePreset
  vbg: VbgMode
  width: number
  height: number
  fps: number
}

/** Result of one bench pass (one configuration). */
export interface BenchPass {
  config: PassConfig
  cycles: BenchCycle[]
  /** Average local audio pipeline latency (ms). */
  avgLocalAudioLatencyMs: number | null
  /** Average local video pipeline latency (ms). */
  avgLocalVideoLatencyMs: number | null
  /** Δ21 = V - A. Positive = video encode slower than audio. */
  diffLocalMs: number | null
}

/** 3x3 sync table: for each (DN preset, VBG mode), the measured Δ = V - A on outgoing tracks. */
export interface SyncTable {
  off:   { none: number; gpu: number; cpu: number }
  sync:  { none: number; gpu: number; cpu: number }
  async: { none: number; gpu: number; cpu: number }
}

/** Full bench profile stored in localStorage. */
export interface BenchProfile {
  /** Schema version. Increment on breaking changes. */
  version: number
  /** ISO timestamp of when the bench was completed. */
  timestamp: string
  /** All pass results (diagnostic — not used for runtime sync). */
  passes: BenchPass[]
  /** The runtime lookup table: syncTable[dn][vbg] = V - A (ms). Apply as videoDelay = max(0, -Δ). */
  syncTable: SyncTable
  /** Ambient noise level (Goertzel mag) seen during calibration. */
  ambientNoise: number
  /** Clock offset between performance.now() and AudioContext.currentTime*1000, at bench start. */
  audioClockOffsetMs: number
  /** Measured outgoing video track fps (median interval). */
  videoTrackFps: number
  /** User agent string for context. */
  userAgent: string
}

/** Bench engine state for the UI. */
export type BenchState =
  | 'idle'
  | 'connecting'
  | 'running'
  | 'done'
  | 'error'

/** Progress info emitted by the engine during a run. */
export interface BenchProgress {
  state: BenchState
  /** Current pass index (0-based). */
  passIndex: number
  /** Total number of passes. */
  totalPasses: number
  /** Current cycle index within the pass. */
  cycleIndex: number
  /** Total cycles per pass. */
  cyclesPerPass: number
  /** Current pass config. */
  currentConfig: PassConfig | null
  /** Accumulated results so far. */
  passes: BenchPass[]
  /** Pre-processing stream: source + bip (before denoise/VBG). */
  sourceStream: MediaStream | null
  /** Post-processing stream: source + bip + traitements (after denoise/VBG). */
  processedStream: MediaStream | null
  /** Cycles completed so far in the current pass (live updates). */
  currentPassCycles: BenchCycle[]
  /** Error message if state === 'error'. */
  error?: string
}

/** The 9-pass matrix: 3 DN presets x 3 VBG modes. */
export const BENCH_PASSES: PassConfig[] = [
  { label: 'DN off',              denoise: 'off',   vbg: 'none', width: 640, height: 360, fps: 22 },
  { label: 'DN off + VBG GPU',    denoise: 'off',   vbg: 'gpu',  width: 640, height: 360, fps: 22 },
  { label: 'DN off + VBG CPU',    denoise: 'off',   vbg: 'cpu',  width: 640, height: 360, fps: 22 },
  { label: 'DN Rapide',           denoise: 'sync',  vbg: 'none', width: 640, height: 360, fps: 22 },
  { label: 'DN Rapide + VBG GPU', denoise: 'sync',  vbg: 'gpu',  width: 640, height: 360, fps: 22 },
  { label: 'DN Rapide + VBG CPU', denoise: 'sync',  vbg: 'cpu',  width: 640, height: 360, fps: 22 },
  { label: 'DN Qualite',          denoise: 'async', vbg: 'none', width: 640, height: 360, fps: 22 },
  { label: 'DN Qualite + VBG GPU',denoise: 'async', vbg: 'gpu',  width: 640, height: 360, fps: 22 },
  { label: 'DN Qualite + VBG CPU',denoise: 'async', vbg: 'cpu',  width: 640, height: 360, fps: 22 },
]

/** One burst point in a sequence. */
export interface BurstPoint {
  offsetMs: number
  type: 'bip1' | 'bip2'
  durationMs: number  // audio beep + video flash duration (simultaneous)
}

/** Burst sequence: bip1 (50ms anchor) + bip2 (30ms short) at +200/+500ms offsets, period 600ms.
 *  bip2 extended to 30ms (from 10ms) so DeepFilterNet3 GRU has time to classify as voiced speech.
 *  Reduced to 4 bursts — empirical data showed Δ21 spread <16ms across 6+ measurements, so 4 is enough. */
export const BURST_SEQUENCE: BurstPoint[] = [
  { offsetMs: 0,    type: 'bip1', durationMs: 50 },
  { offsetMs: 200,  type: 'bip2', durationMs: 30 },
  { offsetMs: 500,  type: 'bip2', durationMs: 30 },
  { offsetMs: 600,  type: 'bip1', durationMs: 50 },
]

export const BURSTS_PER_SEQ = BURST_SEQUENCE.length
/** Number of sequences per pass (1 now — 4 bursts per pass is enough given measurement stability). */
export const SEQUENCES_PER_PASS = 1
/** Total cycles per pass. */
export const CYCLES_PER_PASS = SEQUENCES_PER_PASS * BURSTS_PER_SEQ
export const BEEP_FREQ_HZ = 1000
export const FLASH_THRESHOLD = 180 // luminance threshold for white flash detection (0-255)
export const BEEP_THRESHOLD = 0.05 // Goertzel magnitude threshold
export const LS_BENCH_KEY = 'av-bench-profile'

/** Convert BenchPasses into a SyncTable. Missing cells -> 0. */
export function buildSyncTable(passes: BenchPass[]): SyncTable {
  const table: SyncTable = {
    off:   { none: 0, gpu: 0, cpu: 0 },
    sync:  { none: 0, gpu: 0, cpu: 0 },
    async: { none: 0, gpu: 0, cpu: 0 },
  }
  for (const p of passes) {
    const dn = p.config.denoise
    const vb = p.config.vbg
    table[dn][vb] = p.diffLocalMs ?? 0
  }
  return table
}

/** Lookup helper: compute videoDelay (ms) from syncTable. */
export function videoDelayFromTable(table: SyncTable, dn: DenoisePreset, vb: VbgMode): number {
  const delta = table[dn]?.[vb] ?? 0
  // Δ = V - A. To sync: delay video by max(0, A - V) = max(0, -Δ).
  return Math.max(0, -delta)
}
